/**
 * NsseRadarChart — 10-engagement-indicator radar for US universities.
 *
 * IMPORTANT: NSSE does not publish per-university scores (privacy).
 * What we render here is the mean engagement indicator score across
 * all NSSE-participating universities in this uni's Carnegie
 * classification tier (R1, R2, MA, BA-AS, etc). We stamp the chart
 * with a distinct "TIER-MAPPED" chip and a plain-English caveat so
 * families don't mistake it for HESA/QILT-style verified per-uni
 * data.
 *
 * Source: /api/methodology/nsse-2023/provider?uni_id=X
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Info } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip,
} from "recharts";

export default function NsseRadarChart({ uniId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!uniId) return;
    api.get(`/methodology/nsse-2023/provider?uni_id=${uniId}`)
       .then((r) => setData(r.data))
       .catch(() => setData(null));
  }, [uniId]);

  if (!data) return null;
  const themes = (data.themes || []).filter((t) => t.score != null);
  if (themes.length === 0) {
    return null;
  }

  // NSSE scores are on a 0-60 scale; scale to 0-100 for consistency
  // with the NSS/QILT radars.
  const chartData = themes.map((t) => ({
    theme: t.label,
    "This tier":  Math.round((t.score / 60) * 1000) / 10,
    "R1 national": t.benchmark != null ? Math.round((t.benchmark / 60) * 1000) / 10 : null,
  }));

  return (
    <div className="rounded-lg border border-amber-200 bg-white p-4" data-testid="nsse-radar-chart">
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="inline-block px-1.5 py-0.5 rounded bg-amber-600 text-white text-[9px] uppercase tracking-wider font-mono">
          Tier-mapped
        </span>
        <span className="label-uppercase text-amber-900">
          NSSE {data.cycle || "2023"} · engagement indicators
        </span>
        <span className="text-[10px] font-mono text-amber-800">
          · {data.carnegie_tier_label}
        </span>
      </div>
      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>
          <RadarChart data={chartData} outerRadius="70%">
            <PolarGrid stroke="#e7e5e4" />
            <PolarAngleAxis dataKey="theme"
                            tick={{ fontSize: 10, fill: "#78350F", fontFamily: "monospace" }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]}
                             tick={{ fontSize: 9, fill: "#a8a29e" }} />
            <Radar name="This tier" dataKey="This tier"
                   stroke="#d97706" fill="#d97706" fillOpacity={0.35} />
            <Radar name="R1 national" dataKey="R1 national"
                   stroke="#78350F" fill="#78350F" fillOpacity={0.10} />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-2 border border-amber-200">
        <Info className="h-3.5 w-3.5 text-amber-800 shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-900 leading-snug" data-testid="nsse-tier-mapped-note">
          <strong>Tier-mapped, not per-uni verified.</strong>{" "}
          NSSE (Indiana Uni) doesn't publish per-university scores. This chart shows the mean
          engagement indicator for every NSSE-participating university in this Carnegie tier —
          treat as indicative context, not a verified rating of this specific institution.
        </p>
      </div>
    </div>
  );
}
